// Project the v2 wire `BattleState` (entities map + defs map + scalars)
// into the v1-shape `GameView` the rest of the SPA components
// already consume (`HandRow`, `Field`, `EventLog`, `PreviewPanel`, …).
//
// This is a pure, memoizable selector. The v2 reducer remains the
// source of truth; this file only widens its shape so the existing
// component tree doesn't have to be rewritten.

import type {
  CardView,
  CardType,
  EntityDef,
  EntityRecord,
  GameView,
  Keyword,
  PlayerId,
  PlayerView,
} from "../api/types";
import type { BattleState } from "./battleReducer";

/** Project the v2 BattleState into the v1-shape GameView. Pure
 *  function — call inside a `useMemo`. */
export function selectGameView(state: BattleState): GameView | null {
  if (state.viewer == null) return null;
  const viewer = state.viewer;
  // During the viewer's Declare Attackers step the engine ships its
  // authoritative legal-attacker set on the pending. Trust it verbatim
  // for `can_attack` instead of the local heuristic — the heuristic
  // reads a client-mirrored `summoning_sickness` / keyword set that can
  // drift from the engine across a long match, which would grey out a
  // creature the engine is perfectly willing to swing with.
  const legalAttackers =
    state.pending?.kind === "attackers" && state.pending.player === viewer
      ? new Set(state.pending.attackers)
      : null;
  return {
    viewer,
    turn: state.turn,
    phase: state.phase,
    active_player: state.activePlayer,
    players: [
      buildPlayerView(state, 0, viewer, legalAttackers),
      buildPlayerView(state, 1, viewer, legalAttackers),
    ],
    combat: state.combat,
    pending: state.pending,
    result: state.result,
  };
}

function buildPlayerView(
  state: BattleState,
  seat: PlayerId,
  viewer: PlayerId,
  legalAttackers: Set<number> | null,
): PlayerView {
  const scalars = state.players[seat];
  // `hand` / `deck` / `battle` / `graveyard` / `exile` arrive ordered
  // from the engine — these are the source of truth that the engine's
  // `hand_idx` arithmetic resolves against. Walk them by eid in order
  // and project each entity, dropping any that haven't been revealed
  // (face-down opp battle traps before they spring).
  // The legal-attacker set only describes the active viewer's own
  // creatures; never let it touch the opponent's board projection.
  const legalForSeat = seat === viewer ? legalAttackers : null;
  const project = (eid: number): CardView | null => {
    const rec = state.entities.get(eid);
    if (!rec) return null;
    return projectCard(rec, state.defs, viewer, legalForSeat);
  };
  const eidsToCards = (eids: number[]): CardView[] => {
    const out: CardView[] = [];
    for (const eid of eids) {
      const c = project(eid);
      if (c) out.push(c);
    }
    return out;
  };

  const hand = eidsToCards(scalars.hand);
  const battle = eidsToCards(scalars.battle);
  const deck = eidsToCards(scalars.deck);
  const graveyard = eidsToCards(scalars.graveyard);
  const exile = eidsToCards(scalars.exile);

  // Graveyard / exile are top-first on the v1 wire (most recent
  // first); the engine reverses there. Mirror it client-side so the
  // SPA's existing renderers see the same order.
  graveyard.reverse();
  exile.reverse();

  // Deck sorted by name (alphabetical) for own seat — matches the
  // engine's v1 sort so the SPA's deck viewer reads the same way.
  // Hand / battle stay in engine Vec order — `hand_idx` depends on it.
  deck.sort((a, b) =>
    a.name.localeCompare(b.name) || a.entity_id - b.entity_id,
  );

  return {
    player_id: seat,
    life: scalars.life,
    mana: scalars.mana,
    max_mana: scalars.max_mana,
    deck_size: scalars.deck_size,
    graveyard_size: scalars.graveyard_size,
    exile_size: scalars.exile_size,
    hand_size: scalars.hand_size,
    hand: viewer === seat ? hand : null,
    battle,
    deck: viewer === seat ? deck : null,
    graveyard,
    exile,
  };
}

/** Compose `CardView` from one `EntityRecord` plus its `EntityDef`.
 *  Opponent-controlled face-down cards (def_id null) yield a masked
 *  card. */
export function projectCard(
  rec: EntityRecord,
  defs: Map<string, EntityDef>,
  viewer: PlayerId,
  /** When non-null, the engine's authoritative legal-attacker set for
   *  the current Declare Attackers step. `can_attack` is read straight
   *  off membership in it; otherwise it falls back to the local
   *  heuristic. */
  legalAttackers: Set<number> | null = null,
): CardView {
  const def = rec.def_id ? defs.get(rec.def_id) ?? null : null;
  if (rec.def_id == null || (rec.face_down && rec.controller !== viewer)) {
    return maskedCard(rec);
  }
  const baseAttack = def?.base_attack ?? 0;
  const baseLife = def?.base_life ?? 0;
  const currentAttack = rec.current_attack ?? baseAttack;
  const currentLife = rec.current_life ?? baseLife;
  const damageMarked = rec.damage_marked ?? 0;
  // Clamp at 0: between a Damage event firing and the matching
  // Die/Move pair sweeping the entity off the battlefield, the raw
  // calc can drop below zero. Showing "-5/15" right before a death
  // is misleading; the engine treats marked_damage >= life as dead.
  const remainingLife = Math.max(
    0,
    rec.remaining_life ?? currentLife - damageMarked,
  );
  return {
    entity_id: rec.eid,
    def_id: rec.def_id,
    name: def?.name ?? "",
    type: (def?.type ?? "creature") as CardType,
    cost: def?.cost ?? 0,
    base_attack: baseAttack,
    base_life: baseLife,
    current_attack: currentAttack,
    current_life: currentLife,
    damage_marked: damageMarked,
    remaining_life: remainingLife,
    can_attack:
      legalAttackers != null
        ? legalAttackers.has(rec.eid)
        : computeCanAttack(rec, def),
    is_attacking: rec.is_attacking,
    blocking_against: rec.blocking_against ?? null,
    has_script: def?.has_script ?? false,
    flavor: def?.flavor ?? null,
    rules_text: def?.rules_text ?? null,
    image_url: def?.image_url ?? null,
    faction: def?.faction ?? null,
    race: def?.race ?? null,
    class: def?.class ?? null,
    expansion_set: def?.expansion_set ?? null,
    rarity: def?.rarity ?? null,
    summon_only: def?.summon_only ?? false,
    seal_counters: rec.seal_counters,
    face_down: rec.face_down,
    keywords: rec.keywords,
    castable: rec.castable,
    named_counters: rec.named_counters,
    summoning_sickness: rec.summoning_sickness,
  };
}

/** Trap face-down on opp's side: every identifying field is scrubbed
 *  so the viewer learns only that *something* is there. Faction is left
 *  blank — the v1 server would have inferred from the controller's
 *  faction, but without a wire signal here, we let the renderer's
 *  default carry it (it falls back to a neutral chrome). */
function maskedCard(rec: EntityRecord): CardView {
  return {
    entity_id: rec.eid,
    def_id: "",
    name: "Trap",
    type: "trap",
    cost: 0,
    base_attack: 0,
    base_life: 0,
    current_attack: 0,
    current_life: 0,
    damage_marked: 0,
    remaining_life: 0,
    can_attack: false,
    is_attacking: false,
    has_script: false,
    flavor: null,
    rules_text: null,
    image_url: null,
    faction: null,
    race: null,
    class: null,
    expansion_set: null,
    rarity: null,
    summon_only: false,
    seal_counters: 0,
    face_down: true,
    keywords: rec.keywords,
    castable: false,
    named_counters: [],
    summoning_sickness: false,
  };
}

/** Conservative client-side approximation. The engine remains
 *  authoritative — illegal attacks are still rejected at submit time —
 *  but this lets the UI grey out the obvious cases (non-creatures,
 *  summoning sickness, Rooted, active Sealed). */
function computeCanAttack(rec: EntityRecord, def: EntityDef | null): boolean {
  if (rec.zone !== "battle") return false;
  const type: CardType = (def?.type ?? "creature") as CardType;
  if (type !== "creature") return false;
  if (rec.summoning_sickness) return false;
  const has = (kw: Keyword) => rec.keywords.some((k) => k.name === kw);
  if (has("rooted" as Keyword)) return false;
  if (has("sealed" as Keyword) && rec.seal_counters > 0) return false;
  return true;
}
